import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Metric,
  Field,
  TextInput,
  Select,
} from "@odingard/design-system";
import { EmptyState, ErrorState, LoadingState } from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import type {
  Section,
  DraftResult,
  ComplianceValidation,
  ComplianceSignoff,
  SubmissionPackage,
  EvidencePack,
} from "@odingard/sdk-client";
import { NARRATIVE_TYPES, REVIEW_STAGES, COMMENT_SEVERITIES, REVIEW_RESOLUTIONS } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { proposalApi } from "../../lib/api";
import { DemoModeBanner, LiveModeBadge, GroundedEvidence } from "../../components/ProposalGrounded";

const STAGE_TONE: Record<string, "info" | "warning" | "danger" | "success" | "neutral"> = {
  blue: "info",
  pink: "warning",
  red: "danger",
  gold: "success",
};

export function PaiWorkspaceDetail() {
  const { activeOrg } = useAuth();
  const { workspaceId = "" } = useParams();
  const orgId = activeOrg?.id ?? "";

  const wsState = useAsync(async () => proposalApi.workspace(orgId, workspaceId), [orgId, workspaceId]);
  const progressState = useAsync(async () => proposalApi.workspaceProgress(orgId, workspaceId), [orgId, workspaceId]);
  const sectionsState = useAsync(async () => proposalApi.sections(orgId, workspaceId), [orgId, workspaceId]);

  const w = wsState.data?.data;
  const demo = wsState.data?.demoMode ?? false;
  const progress = progressState.data?.data;
  const sections = sectionsState.data?.data.sections ?? [];

  const [outlineBusy, setOutlineBusy] = useState(false);
  const [outlineErr, setOutlineErr] = useState<string | null>(null);

  async function generateOutline() {
    setOutlineBusy(true);
    setOutlineErr(null);
    try {
      await proposalApi.generateOutline(orgId, workspaceId);
      sectionsState.reload();
      progressState.reload();
    } catch (e) {
      setOutlineErr(e instanceof ApiError ? e.message : "Outline generation failed.");
    } finally {
      setOutlineBusy(false);
    }
  }

  function reloadAll() {
    sectionsState.reload();
    progressState.reload();
    wsState.reload();
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{w?.title ?? "Workspace"}</h1>
          <p>Grounded authoring, color team reviews, compliance sign off, and submission packaging.</p>
        </div>
        <div className="ods-row">
          <LiveModeBadge demoMode={demo} />
          {w && (
            <Link to={`/studio/proposal-ai/opportunities/${w.opportunityId}`}>
              <Button size="sm" variant="secondary">
                Opportunity
              </Button>
            </Link>
          )}
        </div>
      </div>

      <DemoModeBanner show={demo} />
      {wsState.loading && <LoadingState label="Loading workspace…" />}
      {wsState.error && <ErrorState description={wsState.error.message} onRetry={wsState.reload} />}

      {w && (
        <>
          <div className="metric-grid">
            <div className="metric-card">
              <Metric value={w.status} label="Status" />
            </div>
            <div className="metric-card">
              <Metric value={progress?.totalSections ?? sections.length} label="Sections" />
            </div>
            <div className="metric-card">
              <Metric value={`${Math.round(progress?.completionPercent ?? 0)}%`} label="Complete" />
            </div>
            <div className="metric-card">
              <Metric value={progress?.requirementsLinked ?? 0} label="Requirements linked" />
            </div>
          </div>

          <Card>
            <CardHeader
              title="Outline"
              action={
                <Button size="sm" onClick={generateOutline} disabled={outlineBusy}>
                  {outlineBusy ? "Generating…" : sections.length > 0 ? "Regenerate outline" : "Generate outline"}
                </Button>
              }
            />
            <CardBody>
              {outlineErr && <p style={{ color: "var(--ods-color-danger)", margin: 0 }}>{outlineErr}</p>}
              {sectionsState.loading && <LoadingState label="Loading sections…" />}
              {!sectionsState.loading && sections.length === 0 && (
                <EmptyState title="No sections yet" description="Generate an outline to create sections from the solicitation." />
              )}
              {sections.length > 0 && (
                <div className="ods-stack">
                  {sections.map((sec) => (
                    <SectionRow key={sec.id} orgId={orgId} section={sec} onChange={reloadAll} />
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          <ReviewsCard orgId={orgId} workspaceId={workspaceId} />
          <ApprovalCard orgId={orgId} workspaceId={workspaceId} />
          <ComplianceCard orgId={orgId} workspaceId={workspaceId} />
          <SubmissionCard orgId={orgId} workspaceId={workspaceId} />
        </>
      )}
    </>
  );
}

// ---- Section: assignments, knowledge-grounded draft, content, comments, versions
function SectionRow({ orgId, section, onChange }: { orgId: string; section: Section; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: "1px solid var(--ods-color-line)", borderRadius: "var(--ods-radius-md)", padding: "12px" }}>
      <div className="ods-row" style={{ justifyContent: "space-between" }}>
        <div style={{ fontWeight: 600 }}>{section.heading}</div>
        <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
          <Badge tone="neutral">{section.status}</Badge>
          <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Open"}
          </Button>
        </div>
      </div>
      {open && <SectionDetail orgId={orgId} section={section} onChange={onChange} />}
    </div>
  );
}

function SectionDetail({ orgId, section, onChange }: { orgId: string; section: Section; onChange: () => void }) {
  const assignmentsState = useAsync(async () => proposalApi.sectionAssignments(orgId, section.id), [orgId, section.id]);
  const commentsState = useAsync(async () => proposalApi.sectionComments(orgId, section.id), [orgId, section.id]);
  const versionsState = useAsync(async () => proposalApi.sectionVersions(orgId, section.id), [orgId, section.id]);
  const knowledgeState = useAsync(async () => proposalApi.knowledgeAssets(orgId), [orgId]);

  const assignments = assignmentsState.data?.data.assignments ?? [];
  const comments = commentsState.data?.data.comments ?? [];
  const versions = versionsState.data?.data.versions ?? [];
  const approvedAssets = (knowledgeState.data?.data.assets ?? []).filter((a) => a.approvalState === "approved");

  // Assignment
  const { user } = useAuth();
  const [assignRole, setAssignRole] = useState("owner");
  const [assignBusy, setAssignBusy] = useState(false);

  async function assign() {
    setAssignBusy(true);
    try {
      await proposalApi.assignSection(orgId, section.id, user?.id ?? "", assignRole);
      assignmentsState.reload();
    } finally {
      setAssignBusy(false);
    }
  }

  // Comment
  const [comment, setComment] = useState("");
  async function addComment() {
    if (!comment.trim()) return;
    await proposalApi.addSectionComment(orgId, section.id, comment.trim());
    setComment("");
    commentsState.reload();
  }
  async function resolveComment(id: string) {
    await proposalApi.resolveSectionComment(orgId, id);
    commentsState.reload();
  }

  // Draft
  const [narrativeType, setNarrativeType] = useState<string>(NARRATIVE_TYPES[1]);
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftErr, setDraftErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftResult | null>(null);

  function toggleAsset(id: string) {
    setSelectedAssets((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function generateDraft() {
    setDraftBusy(true);
    setDraftErr(null);
    setDraft(null);
    try {
      const res = await proposalApi.draftSection(orgId, section.id, {
        narrativeType,
        action: "draft",
        sources: {
          requirementIds: section.requirementIds,
          knowledgeAssetIds: selectedAssets,
        },
      });
      setDraft(res.data);
      versionsState.reload();
      onChange();
    } catch (e) {
      setDraftErr(e instanceof ApiError ? e.message : "Draft failed.");
    } finally {
      setDraftBusy(false);
    }
  }

  const draftGrounded = draft?.grounded && (draft.version?.content || draft.content);
  const draftContent = draft?.version?.content ?? draft?.content ?? "";
  const draftMeta = draft?.version?.aiMetadata ?? null;

  return (
    <div className="ods-stack" style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed var(--ods-color-line)" }}>
      {/* Assignments */}
      <div>
        <strong>Ownership &amp; assignments</strong>
        <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap", margin: "6px 0" }}>
          {assignments.length === 0 && <span style={{ color: "var(--ods-color-muted)" }}>No assignments.</span>}
          {assignments.map((a) => (
            <Badge key={a.id} tone="neutral">
              {a.role}
            </Badge>
          ))}
        </div>
        <div className="ods-row" style={{ gap: "var(--ods-space-2)", alignItems: "flex-end" }}>
          <div style={{ minWidth: 160 }}>
            <Field label="Assign me as">
              {(id) => (
                <Select id={id} value={assignRole} onChange={(e) => setAssignRole(e.target.value)}>
                  <option value="owner">owner</option>
                  <option value="contributor">contributor</option>
                  <option value="reviewer">reviewer</option>
                </Select>
              )}
            </Field>
          </div>
          <Button size="sm" variant="secondary" onClick={assign} disabled={assignBusy}>
            Assign
          </Button>
        </div>
      </div>

      {/* Grounded draft */}
      <div>
        <strong>Grounded AI draft</strong>
        <div className="ods-stack" style={{ margin: "6px 0" }}>
          <Field label="Narrative type">
            {(id) => (
              <Select id={id} value={narrativeType} onChange={(e) => setNarrativeType(e.target.value)}>
                {NARRATIVE_TYPES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div>
            <div style={{ fontSize: "var(--ods-text-sm)", color: "var(--ods-color-muted)", marginBottom: 4 }}>
              Approved knowledge sources (grounding). Drafting fails closed without approved sources.
            </div>
            {approvedAssets.length === 0 && (
              <span style={{ color: "var(--ods-color-warning)", fontSize: "var(--ods-text-sm)" }}>
                No approved knowledge assets. Add and approve sources in the Knowledge library first.
              </span>
            )}
            <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap" }}>
              {approvedAssets.map((a) => (
                <label key={a.id} className="ods-row" style={{ gap: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={selectedAssets.includes(a.id)} onChange={() => toggleAsset(a.id)} />
                  <span style={{ fontSize: "var(--ods-text-sm)" }}>{a.title}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Button size="sm" onClick={generateDraft} disabled={draftBusy}>
              {draftBusy ? "Drafting…" : "Generate grounded draft"}
            </Button>
          </div>
          {draftErr && <p style={{ color: "var(--ods-color-danger)", margin: 0 }}>{draftErr}</p>}
          {draft && !draftGrounded && (
            <div className="ods-failclosed" role="status">
              <strong>Failed closed:</strong> {draft.content || "Insufficient approved source material to ground this draft."}
            </div>
          )}
          {draft && draftGrounded && (
            <div className="ods-stack">
              <GroundedEvidence
                meta={{
                  ...(draftMeta?.provider ? { provider: draftMeta.provider } : {}),
                  ...(draftMeta?.promptVersion ? { promptVersion: draftMeta.promptVersion } : {}),
                  ...((draftMeta?.confidence ?? draft.confidence) != null
                    ? { confidence: draftMeta?.confidence ?? draft.confidence }
                    : {}),
                }}
              />
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  background: "var(--ods-color-surface-2)",
                  padding: 12,
                  borderRadius: "var(--ods-radius-md)",
                  margin: 0,
                }}
              >
                {draftContent}
              </pre>
              {(draft.version?.sourceReferences ?? draft.sourceReferences ?? []).length > 0 && (
                <div>
                  <strong style={{ fontSize: "var(--ods-text-sm)" }}>Sources</strong>
                  <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                    {(draft.version?.sourceReferences ?? draft.sourceReferences ?? []).map((sr, i) => (
                      <li key={i} style={{ fontSize: "var(--ods-text-sm)" }}>
                        <strong>{sr.label}</strong>: {sr.snippet}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Version history */}
      <div>
        <strong>Version history</strong>
        {versionsState.loading && <LoadingState label="Loading versions…" />}
        {!versionsState.loading && versions.length === 0 && (
          <p style={{ color: "var(--ods-color-muted)", margin: "6px 0" }}>No versions yet.</p>
        )}
        {versions.length > 0 && (
          <ul style={{ margin: "6px 0", paddingLeft: 18 }}>
            {versions.map((v) => (
              <li key={v.id} style={{ fontSize: "var(--ods-text-sm)" }}>
                v{v.versionNumber} · {v.wordCount} words · {v.aiGenerated ? "AI" : "manual"}
                {v.changeSummary ? ` · ${v.changeSummary}` : ""}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Comments */}
      <div>
        <strong>Comments</strong>
        <div className="ods-stack" style={{ margin: "6px 0" }}>
          {comments.length === 0 && <span style={{ color: "var(--ods-color-muted)" }}>No comments.</span>}
          {comments.map((c) => (
            <div key={c.id} className="ods-row" style={{ justifyContent: "space-between", gap: "var(--ods-space-2)" }}>
              <span style={{ fontSize: "var(--ods-text-sm)", textDecoration: c.resolved ? "line-through" : "none" }}>
                {c.body}
              </span>
              {!c.resolved && (
                <Button size="sm" variant="ghost" onClick={() => resolveComment(c.id)}>
                  Resolve
                </Button>
              )}
            </div>
          ))}
          <div className="ods-row" style={{ gap: "var(--ods-space-2)", alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <Field label="Add comment">
                {(id) => <TextInput id={id} value={comment} onChange={(e) => setComment(e.target.value)} />}
              </Field>
            </div>
            <Button size="sm" variant="secondary" onClick={addComment} disabled={!comment.trim()}>
              Comment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Reviews: Blue/Pink/Red/Gold cycles + findings + resolution
function ReviewsCard({ orgId, workspaceId }: { orgId: string; workspaceId: string }) {
  const cyclesState = useAsync(async () => proposalApi.reviewCycles(orgId, workspaceId), [orgId, workspaceId]);
  const cycles = cyclesState.data?.data.cycles ?? [];
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function start(stage: (typeof REVIEW_STAGES)[number]) {
    setBusy(true);
    setErr(null);
    try {
      await proposalApi.startReviewCycle(orgId, workspaceId, stage);
      cyclesState.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not start review.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader title="Color team reviews" />
      <CardBody>
        <div className="ods-stack">
          <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap" }}>
            {REVIEW_STAGES.map((s) => (
              <Button key={s} size="sm" variant="secondary" disabled={busy} onClick={() => start(s)}>
                Start {s} review
              </Button>
            ))}
          </div>
          {err && <p style={{ color: "var(--ods-color-danger)", margin: 0 }}>{err}</p>}
          {cyclesState.loading && <LoadingState label="Loading reviews…" />}
          {!cyclesState.loading && cycles.length === 0 && (
            <EmptyState title="No review cycles" description="Start a Blue/Pink/Red/Gold review." />
          )}
          {cycles.map((c) => (
            <ReviewCycleRow key={c.id} orgId={orgId} cycleId={c.id} stage={c.stage} status={c.status} onChange={cyclesState.reload} />
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function ReviewCycleRow({
  orgId,
  cycleId,
  stage,
  status,
  onChange,
}: {
  orgId: string;
  cycleId: string;
  stage: string;
  status: string;
  onChange: () => void;
}) {
  const commentsState = useAsync(async () => proposalApi.reviewComments(orgId, cycleId), [orgId, cycleId]);
  const comments = commentsState.data?.data.comments ?? [];
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState<string>("major");
  const [busy, setBusy] = useState(false);

  async function addFinding() {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await proposalApi.addReviewComment(orgId, cycleId, body.trim(), severity);
      setBody("");
      commentsState.reload();
    } finally {
      setBusy(false);
    }
  }
  async function resolve(id: string, resolutionType: string) {
    await proposalApi.resolveReviewComment(orgId, id, resolutionType);
    commentsState.reload();
  }
  async function complete() {
    setBusy(true);
    try {
      await proposalApi.completeReviewCycle(orgId, cycleId);
      onChange();
      commentsState.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ border: "1px solid var(--ods-color-line)", borderRadius: "var(--ods-radius-md)", padding: 12 }}>
      <div className="ods-row" style={{ justifyContent: "space-between" }}>
        <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
          <Badge tone={STAGE_TONE[stage] ?? "neutral"}>{stage}</Badge>
          <Badge tone="neutral">{status}</Badge>
        </div>
        {status !== "complete" && (
          <Button size="sm" variant="ghost" onClick={complete} disabled={busy}>
            Complete cycle
          </Button>
        )}
      </div>
      <div className="ods-stack" style={{ marginTop: 8 }}>
        {comments.map((c) => (
          <div key={c.id} className="ods-row" style={{ justifyContent: "space-between", gap: "var(--ods-space-2)" }}>
            <span style={{ fontSize: "var(--ods-text-sm)" }}>
              <Badge tone={c.severity === "critical" ? "danger" : c.severity === "major" ? "warning" : "neutral"}>
                {c.severity}
              </Badge>{" "}
              {c.body} <em style={{ color: "var(--ods-color-muted)" }}>({c.status})</em>
            </span>
            {c.status !== "resolved" && (
              <Select value="" onChange={(e) => e.target.value && resolve(c.id, e.target.value)}>
                <option value="">Resolve…</option>
                {REVIEW_RESOLUTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            )}
          </div>
        ))}
        <div className="ods-row" style={{ gap: "var(--ods-space-2)", alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <Field label="Add finding">
              {(id) => <TextInput id={id} value={body} onChange={(e) => setBody(e.target.value)} />}
            </Field>
          </div>
          <div style={{ minWidth: 130 }}>
            <Field label="Severity">
              {(id) => (
                <Select id={id} value={severity} onChange={(e) => setSeverity(e.target.value)}>
                  {COMMENT_SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <Button size="sm" variant="secondary" onClick={addFinding} disabled={busy || !body.trim()}>
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- Executive approval
function ApprovalCard({ orgId, workspaceId }: { orgId: string; workspaceId: string }) {
  const state = useAsync(async () => proposalApi.approvals(orgId, workspaceId), [orgId, workspaceId]);
  const approvals = state.data?.data.approvals ?? [];
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      state.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Action failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Executive approval"
        action={
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => run(() => proposalApi.requestApproval(orgId, workspaceId))}>
            Request approval
          </Button>
        }
      />
      <CardBody>
        <div className="ods-stack">
          {err && <p style={{ color: "var(--ods-color-danger)", margin: 0 }}>{err}</p>}
          {state.loading && <LoadingState label="Loading approvals…" />}
          {!state.loading && approvals.length === 0 && (
            <EmptyState title="No approvals" description="Request executive approval when the proposal is ready." />
          )}
          {approvals.map((a) => (
            <div key={a.id} className="ods-row" style={{ justifyContent: "space-between", gap: "var(--ods-space-2)" }}>
              <Badge tone={a.status === "approved" ? "success" : a.status === "rejected" ? "danger" : "info"}>
                {a.status}
              </Badge>
              {(a.status === "requested" || a.status === "pending") && (
                <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                  <Button size="sm" onClick={() => run(() => proposalApi.decideApproval(orgId, a.id, "approved"))} disabled={busy}>
                    Approve
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => run(() => proposalApi.decideApproval(orgId, a.id, "returned"))} disabled={busy}>
                    Return
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

// ---- Compliance validation + sign-off
function ComplianceCard({ orgId, workspaceId }: { orgId: string; workspaceId: string }) {
  const [validation, setValidation] = useState<ComplianceValidation | null>(null);
  const [signoff, setSignoff] = useState<ComplianceSignoff | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function validate() {
    setBusy(true);
    setErr(null);
    try {
      const res = await proposalApi.runComplianceValidation(orgId, workspaceId);
      setValidation(res.data);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Validation failed.");
    } finally {
      setBusy(false);
    }
  }
  async function sign() {
    if (!validation) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await proposalApi.signoffCompliance(orgId, workspaceId, validation.id);
      setSignoff(res.data);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "The sign off failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader title="Compliance validation and sign off" />
      <CardBody>
        <div className="ods-stack">
          <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
            <Button size="sm" variant="secondary" onClick={validate} disabled={busy}>
              Run compliance validation
            </Button>
            <Button size="sm" onClick={sign} disabled={busy || !validation}>
              Complete sign off
            </Button>
          </div>
          {err && <p style={{ color: "var(--ods-color-danger)", margin: 0 }}>{err}</p>}
          {validation && (
            <div className="metric-grid">
              <div className="metric-card">
                <Metric value={validation.totalFindings} label="Findings" />
              </div>
              <div className="metric-card">
                <Metric value={validation.criticalFindings} label="Critical" tone="var(--ods-color-danger)" />
              </div>
              <div className="metric-card">
                <Metric value={validation.blocking ? "Yes" : "No"} label="Blocking" />
              </div>
            </div>
          )}
          {signoff && (
            <div className="ods-stack" style={{ gap: 4 }}>
              <Badge tone={signoff.result === "passed" || signoff.result === "signed_off" ? "success" : "warning"}>
                Sign off {signoff.result} · seal {signoff.sealedHash.slice(0, 12)}…
              </Badge>
              <div style={{ fontSize: "var(--ods-text-sm)", color: "var(--ods-color-muted)" }}>
                Sign off ID (use below to assemble the package):{" "}
                <code style={{ userSelect: "all" }}>{signoff.id}</code>
              </div>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// ---- Submission package + DOCX/PDF + portal readiness + evidence pack
function SubmissionCard({ orgId, workspaceId }: { orgId: string; workspaceId: string }) {
  const packagesState = useAsync(async () => proposalApi.submissionPackages(orgId, workspaceId), [orgId, workspaceId]);
  const packagesData = packagesState.data?.data.packages;
  const packages = useMemo(() => packagesData ?? [], [packagesData]);

  const [versionLabel, setVersionLabel] = useState("v1.0");
  const [signoffId, setSignoffId] = useState("");
  const [pkg, setPkg] = useState<SubmissionPackage | null>(null);
  const [portal, setPortal] = useState<{ id: string; ready: boolean } | null>(null);
  const [evidence, setEvidence] = useState<EvidencePack | null>(null);
  const [evidenceVerified, setEvidenceVerified] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const primaryPackageId = useMemo(() => {
    const docx = packages.find((p) => p.format === "docx");
    return docx?.id ?? packages[0]?.id ?? pkg?.artifacts.find((a) => a.format === "docx")?.id ?? null;
  }, [packages, pkg]);

  async function assemble() {
    if (!signoffId.trim()) {
      setErr("A compliance sign off ID is required to assemble a package.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const res = await proposalApi.assembleSubmissionPackage(orgId, workspaceId, versionLabel.trim(), signoffId.trim());
      setPkg(res.data);
      packagesState.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Assembly failed.");
    } finally {
      setBusy(false);
    }
  }
  async function checkPortal() {
    if (!primaryPackageId) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await proposalApi.validatePortalReadiness(orgId, primaryPackageId);
      setPortal({ id: res.data.packageId, ready: res.data.ready });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Portal validation failed.");
    } finally {
      setBusy(false);
    }
  }
  async function makeEvidence() {
    setBusy(true);
    setErr(null);
    try {
      const res = await proposalApi.generateEvidencePack(orgId, workspaceId);
      setEvidence(res.data);
      setEvidenceVerified(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Evidence pack failed.");
    } finally {
      setBusy(false);
    }
  }
  async function verifyEvidence() {
    if (!evidence) return;
    setBusy(true);
    try {
      const res = await proposalApi.verifyEvidencePack(orgId, evidence.id);
      setEvidenceVerified(res.data.checksumValid && res.data.auditChainIntact);
    } finally {
      setBusy(false);
    }
  }

  const artifacts = pkg?.artifacts ?? packages;

  return (
    <Card>
      <CardHeader title="Submission package &amp; evidence" />
      <CardBody>
        <div className="ods-stack">
          <p style={{ margin: 0, color: "var(--ods-color-muted)" }}>
            Assemble the DOCX/PDF package from a compliance sign off, check portal readiness, and generate a
            tamper evident evidence pack. Submission stays a human action.
          </p>
          <div className="ods-row" style={{ gap: "var(--ods-space-2)", alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ minWidth: 120 }}>
              <Field label="Version label">
                {(id) => <TextInput id={id} value={versionLabel} onChange={(e) => setVersionLabel(e.target.value)} />}
              </Field>
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Field label="Compliance sign off ID" help="From the compliance sign off above.">
                {(id) => <TextInput id={id} value={signoffId} onChange={(e) => setSignoffId(e.target.value)} />}
              </Field>
            </div>
            <Button size="sm" onClick={assemble} disabled={busy}>
              Assemble package
            </Button>
          </div>
          {err && <p style={{ color: "var(--ods-color-danger)", margin: 0 }}>{err}</p>}

          {artifacts.length > 0 && (
            <div className="ods-stack">
              <strong style={{ fontSize: "var(--ods-text-sm)" }}>Artifacts</strong>
              {artifacts.map((a) => (
                <div key={a.id} className="ods-row" style={{ justifyContent: "space-between", fontSize: "var(--ods-text-sm)" }}>
                  <Badge tone="neutral">{a.format.toUpperCase()}</Badge>
                  <span style={{ color: "var(--ods-color-muted)" }}>{a.byteSize} bytes</span>
                  <span style={{ color: "var(--ods-color-muted)", fontFamily: "monospace" }}>{a.checksum.slice(0, 16)}…</span>
                </div>
              ))}
            </div>
          )}

          <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap" }}>
            <Button size="sm" variant="secondary" onClick={checkPortal} disabled={busy || !primaryPackageId}>
              Check portal readiness
            </Button>
            <Button size="sm" variant="secondary" onClick={makeEvidence} disabled={busy}>
              Generate evidence pack
            </Button>
            {evidence && (
              <Button size="sm" variant="ghost" onClick={verifyEvidence} disabled={busy}>
                Verify evidence pack
              </Button>
            )}
          </div>

          {portal && (
            <Badge tone={portal.ready ? "success" : "warning"}>
              {portal.ready ? "Ready for the portal" : "Not ready for the portal"}
            </Badge>
          )}
          {evidence && (
            <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap" }}>
              <Badge tone={evidence.auditChainIntact ? "success" : "danger"}>
                audit chain {evidence.auditChainIntact ? "intact" : "broken"}
              </Badge>
              <Badge tone="neutral">{evidence.byteSize} bytes</Badge>
              {evidenceVerified != null && (
                <Badge tone={evidenceVerified ? "success" : "danger"}>
                  {evidenceVerified ? "verified" : "verification failed"}
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
